import { forwardRef, useRef, type ChangeEvent, type InputHTMLAttributes } from 'react';
import { Input } from './input';

// Поле телефона с маской российского номера.
//
// Зачем. Раньше это был обычный текстовый инпут: человек вводил номер как привык -
// «+7 999 000 11 22», «+7-999-000-11-22», «8 (999) 000-11-22» - и всё это уезжало на сервер
// как есть. Нормализация там была (цифры + 8→7), но проверки длины не было ни на клиенте,
// ни на сервере: опечатка в десять цифр молча доходила до шлюза и падала там невнятной
// ошибкой мессенджера, уже без связи с тем, что человек не дописал цифру.
//
// Пока поддерживаем ТОЛЬКО +7 - осознанное упрощение: клиенты и их гости в РФ, а маска под
// произвольную страну требует таблицы форматов и выбора кода, то есть отдельной задачи.
// Наружу компонент отдаёт чистые цифры (`79990001122`), показывает `+7 (999) 000-11-22`.

/** Длина российского номера в цифрах, включая код страны. */
export const RU_PHONE_DIGITS = 11;

/**
 * Всё, что человек мог набрать или вставить, → цифры российского номера.
 *
 * Терпимо к вводу: `+7…`, `8…`, `7…` и просто `916…` (человек начал с кода оператора).
 * Разделители, скобки и плюс выбрасываются - вставка из адресной книги или из письма
 * не должна ничего ломать.
 */
export function toRuDigits(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';
  // 8 в начале - привычная местная запись того же номера.
  if (digits.startsWith('8') && digits.length > 1) digits = `7${digits.slice(1)}`;
  // Человек начал с кода оператора: 916… Дописываем код страны сами, иначе маска
  // разъедется и он увидит «+9 (166) 271-591».
  else if (!digits.startsWith('7')) digits = `7${digits}`;
  return digits.slice(0, RU_PHONE_DIGITS);
}

/** Цифры → `+7 (999) 000-11-22`. Незаконченный номер форматируется по мере ввода. */
export function formatRuPhone(digits: string): string {
  if (!digits) return '';
  const d = digits.slice(0, RU_PHONE_DIGITS);
  let out = '+7';
  if (d.length > 1) out += ` (${d.slice(1, 4)}`;
  if (d.length >= 5) out += `) ${d.slice(4, 7)}`;
  if (d.length >= 8) out += `-${d.slice(7, 9)}`;
  if (d.length >= 10) out += `-${d.slice(9, 11)}`;
  return out;
}

export function isCompleteRuPhone(digits: string): boolean {
  return digits.length === RU_PHONE_DIGITS && digits.startsWith('7');
}

type PhoneInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'value' | 'onChange'> & {
  /** Чистые цифры, напр. `79990001122`. Пустая строка - поле не заполнено. */
  value: string;
  /** Отдаёт цифры, а не отформатированную строку: потребителю форматирование не нужно. */
  onChange: (digits: string) => void;
};

export const PhoneInput = forwardRef<HTMLInputElement, PhoneInputProps>(
  ({ value, onChange, ...props }, ref) => {
    const inputRef = useRef<HTMLInputElement | null>(null);

    function handle(e: ChangeEvent<HTMLInputElement>): void {
      const el = e.target;
      const caret = el.selectionStart ?? el.value.length;
      // Сколько цифр стояло левее курсора - по нему вернём курсор на место после
      // переформатирования. Иначе он прыгает в конец на каждой правке в середине номера.
      const digitsBefore = el.value.slice(0, caret).replace(/\D/g, '').length;

      const digits = toRuDigits(el.value);
      onChange(digits);

      const formatted = formatRuPhone(digits);
      // Код страны компонент дописывает сам - значит слева от курсора стало на цифру больше.
      const added = digits.length - el.value.replace(/\D/g, '').length;
      const target = caretAfterDigits(formatted, digitsBefore + Math.max(0, added));
      requestAnimationFrame(() => {
        const node = inputRef.current;
        if (node && node.value === formatted) node.setSelectionRange(target, target);
      });
    }

    return (
      <Input
        {...props}
        ref={(node) => {
          inputRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={formatRuPhone(value)}
        onChange={handle}
        placeholder={props.placeholder ?? '+7 (999) 000-11-22'}
      />
    );
  },
);
PhoneInput.displayName = 'PhoneInput';

/** Позиция в отформатированной строке сразу после n-й цифры. */
function caretAfterDigits(formatted: string, n: number): number {
  if (n <= 0) return formatted.length ? 2 : 0; // сразу после «+7»
  let seen = 0;
  for (let i = 0; i < formatted.length; i += 1) {
    if (/\d/.test(formatted[i]!)) {
      seen += 1;
      if (seen === n) return i + 1;
    }
  }
  return formatted.length;
}
